// Shared activity-log row rendering — one stamp/redemption/correction event.
// Used by LogsPage (tenant-wide feed) and CustomerDetailModal's "Activity"
// section (one customer's full history) so the two surfaces render events
// identically. Pulls in logsPage.css since that's where `.logs-row*` /
// `.logs-source-badge*` live — importing it here (not just from LogsPage)
// means the modal renders correctly even when opened from a page that never
// mounted LogsPage (e.g. CustomersPage).

import type { KeyboardEvent } from 'react'

import type { ActivityLogItem } from '../hooks/useStampHistory.js'
import '../pages/logsPage.css'

export function LogRow({
  item,
  onOpen,
  hideCustomer = false,
}: {
  item: ActivityLogItem
  onOpen: () => void
  /** Customer lifecycle (2026-09-05) — suppress the customer name/click-
   *  through when this row is already rendered inside that customer's own
   *  detail modal, where the name would be redundant and "open customer"
   *  would just reopen the modal it's already in. */
  hideCustomer?: boolean
}) {
  const customer = customerLabel(item)
  const when = formatWhen(item.timestamp)
  const meta = [when, item.branch_name, item.staff_display_name]
    .filter(Boolean)
    .join(' · ')

  function onKeyDown(e: KeyboardEvent<HTMLLIElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }

  const className = hideCustomer ? 'logs-row' : 'logs-row logs-row-clickable'
  const interactive = hideCustomer
    ? {}
    : { role: 'button' as const, tabIndex: 0, onClick: onOpen, onKeyDown }

  if (item.kind === 'redemption') {
    return (
      <li className={className} {...interactive}>
        <div className="logs-row-main">
          <div className="logs-row-customer">
            {!hideCustomer && customer}
            <span className="logs-source-badge logs-source-badge-redemption">
              Reward claimed
            </span>
          </div>
          <div className="logs-row-meta">{meta}</div>
        </div>
        <div className="logs-row-reward">{item.reward_text}</div>
      </li>
    )
  }

  if (item.kind === 'adjustment') {
    const parts = [
      item.delta !== 0
        ? `${item.previous_count} → ${item.new_count} stamps`
        : null,
      item.secret_delta !== 0
        ? `secret ${item.secret_delta > 0 ? '+' : ''}${item.secret_delta}`
        : null,
    ].filter(Boolean)
    return (
      <li className={className} {...interactive}>
        <div className="logs-row-main">
          <div className="logs-row-customer">
            {!hideCustomer && customer}
            <span className="logs-source-badge logs-source-badge-adjustment">
              Correction
            </span>
          </div>
          <div className="logs-row-meta">
            {meta}
            {meta ? ' · ' : ''}
            {item.reason}
          </div>
        </div>
        <div className="logs-row-progress">{parts.join(' · ')}</div>
      </li>
    )
  }

  const isBatch = item.stamps_added > 1
  return (
    <li className={className} {...interactive}>
      <div className="logs-row-main">
        <div className="logs-row-customer">
          {!hideCustomer && customer}
          {isBatch && (
            <span className="logs-source-badge logs-source-badge-batch">
              {item.stamps_added} stamps
            </span>
          )}
          {item.secret_stamp_count > 0 && (
            <span className="logs-source-badge logs-source-badge-secret">
              {isBatch ? `${item.secret_stamp_count} off-menu` : 'Off-menu'}
            </span>
          )}
          {sourceBadge(item.source) && (
            <span className="logs-source-badge">{sourceBadge(item.source)}</span>
          )}
        </div>
        <div className="logs-row-meta">{meta}</div>
      </div>
      <div className="logs-row-progress">
        {isBatch ? `${item.stamp_from} → ${item.stamp_to}` : item.stamp_to} /{' '}
        {item.total_stamps}
      </div>
    </li>
  )
}

// Label non-purchase stamps so the owner can tell at a glance what each stamp
// came from. A plain staff_scan (a purchase) stays unbadged — it's the norm.
function sourceBadge(source: string): string | null {
  switch (source) {
    case 'referral_welcome':
      return 'Referral bonus'
    case 'referral_reward':
      return 'Referral reward'
    case 'manual_code':
      return 'Manual'
    default:
      return null
  }
}

function customerLabel(item: {
  customer_first_name: string | null
  customer_last_name: string | null
  card_code: string
}): string {
  const name = [item.customer_first_name, item.customer_last_name]
    .filter((s): s is string => !!s && s.trim().length > 0)
    .join(' ')
    .trim()
  return name || item.card_code
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // Show PH-local time (UTC+8). The owner thinks in their counter's
  // wall-clock; we format here rather than passing raw ISO. Include the
  // year once ranges can span past the current one ("All time").
  const now = new Date()
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear()
  return d.toLocaleString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })
}
